import {t} from 'i18next';
import {useMemo} from 'react';
import {
  BUDGET_ESTIMATED,
  BUSINESS_INTELLIGENCE_PERMISSION_CODES_CONSTANTS,
  CLIENT_PERMISSION_CODES_CONSTANTS,
  ECA_PERMISSION_CODES_CONSTANTS,
  EVIDENCE_SOURCE_MANAGEMENT,
  MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS,
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
  REPORT_PERMISSION_CODES_CONSTANTS,
} from '../constants/constants';

export type PermissionType =
  (typeof PERMISSION_TYPE)[keyof typeof PERMISSION_TYPE];

export type PermissionCode =
  | REPORT_PERMISSION_CODES_CONSTANTS
  | CLIENT_PERMISSION_CODES_CONSTANTS
  | BUDGET_ESTIMATED
  | EVIDENCE_SOURCE_MANAGEMENT
  | ECA_PERMISSION_CODES_CONSTANTS
  | MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS
  | BUSINESS_INTELLIGENCE_PERMISSION_CODES_CONSTANTS
  | string;

export interface PermissionItem {
  type: PermissionType;
  permissionCode: PermissionCode;
  permissionId: string;
  name: string;
  description?: string;
  infoText?: string;
  remarks?: string;
  footerNotes?: string;
  isVisible?: boolean;
  checked?: boolean;
  category: PERMISSION_GROUP_CATEGORY[];
  disableToolTip?: boolean;
}

export type PermissionMasterConfigType = Record<
  PermissionType,
  {
    perm_categories: PermissionType[];
    perm_cat_config: Record<
      PermissionType,
      {
        title?: string;
        footerNotes?: string;
        desc?: string;
        isOverriden?: boolean;
        defaultIsOverridenChecked?: boolean;
        defaultCheckedPerms?: PermissionCode[];
      }
    >;
  }
>;

export enum EXCLUDED_PERMISSION_CODE {
  REPORT_BUDGET_TRACKER_VIEW = 'REPORT_BUDGET_TRACKER_VIEW',
}

export const excludedPermissionCodes = [
  EXCLUDED_PERMISSION_CODE.REPORT_BUDGET_TRACKER_VIEW,
];

export const usePermissionGroupConfig = () => {
  const permissionMasterConfig = useMemo(
    (): PermissionMasterConfigType => ({
      [PERMISSION_GROUP_CATEGORY.CLIENT]: {
        perm_categories: [
          PERMISSION_TYPE.SYSTEM,
          PERMISSION_TYPE.MANAGE_PROJECT,
          PERMISSION_TYPE.REPORTS,
          PERMISSION_TYPE.BUSINESS_INTELLIGENCE,
        ],
        perm_cat_config: {
          [PERMISSION_TYPE.SYSTEM]: {
            title: t('addNewPermissionGroupPage.system'),
          },
          [PERMISSION_TYPE.MANAGE_PROJECT]: {
            title: t('addNewPermissionGroupPage.projectAdministration'),
          },
          [PERMISSION_TYPE.REPORTS]: {
            title: t('addNewPermissionGroupPage.reporting'),
          },
          [PERMISSION_TYPE.BUSINESS_INTELLIGENCE]: {
            title: t('addNewPermissionGroupPage.businessIntelligence'),
            footerNotes: t(
              'addNewPermissionGroupPage.permissions.availableToConsilioOnly'
            ),
          },
        },
      },
      [PERMISSION_GROUP_CATEGORY.PROJECT]: {
        perm_categories: [
          PERMISSION_TYPE.MANAGE_PROJECT,
          PERMISSION_TYPE.REPORTS,
          PERMISSION_TYPE.ECI,
          PERMISSION_TYPE.BUSINESS_INTELLIGENCE,
        ],
        perm_cat_config: {
          [PERMISSION_TYPE.MANAGE_PROJECT]: {
            title: t('addNewPermissionGroupPage.manageProject'),
            desc: t('addNewPermissionGroupPage.manageProjectDesc'),
            isOverriden: true,
            defaultIsOverridenChecked: false,
            defaultCheckedPerms: [],
          },
          [PERMISSION_TYPE.REPORTS]: {
            title: t('addNewPermissionGroupPage.reporting'),
            desc: t('addNewPermissionGroupPage.manageProjectDesc'),
            isOverriden: true,
            defaultIsOverridenChecked: false,
            defaultCheckedPerms: [],
          },
          [PERMISSION_TYPE.BUDGET]: {
            title: t('addNewPermissionGroupPage.budgetConsumption'),
            desc: t('addNewPermissionGroupPage.budgetConsumptionDesc'),
          },
          [PERMISSION_TYPE.ECI]: {
            title: t('addNewPermissionGroupPage.eci'),
            isOverriden: true,
            defaultCheckedPerms: [
              ECA_PERMISSION_CODES_CONSTANTS.ECI_SEARCHES,
              ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCHES,
              ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_PREVIEW,
              ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_TAGGING,
            ],
          },
          [PERMISSION_TYPE.EVIDENCE]: {
            title: t('addNewPermissionGroupPage.evidenceSourceManagement'),
            isOverriden: true,
          },
          [PERMISSION_TYPE.BUSINESS_INTELLIGENCE]: {
            title: t('addNewPermissionGroupPage.businessIntelligence'),
            desc: t('addNewPermissionGroupPage.businessIntelligenceDesc'),
            isOverriden: true,
            defaultIsOverridenChecked: false,
            defaultCheckedPerms: [],
          },
        },
      },
    }),
    []
  );

  const permissionList = useMemo(
    (): Record<PermissionType, PermissionItem[]> => ({
      [PERMISSION_TYPE.SYSTEM]: [
        {
          type: PERMISSION_TYPE.SYSTEM,
          permissionCode: CLIENT_PERMISSION_CODES_CONSTANTS.CLIENTS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece15',
          name: t(
            'addNewPermissionGroupPage.permissions.manageClientSettingsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.manageClientSettingsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.CLIENT],
          infoText: t(
            'addNewPermissionGroupPage.permissions.manageClientSettingsInfoText'
          ),
        },
        {
          type: PERMISSION_TYPE.SYSTEM,
          permissionCode:
            CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece49',
          name: t(
            'addNewPermissionGroupPage.permissions.managePermissionGroupsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.managePermissionGroupsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.CLIENT],
          infoText: t(
            'addNewPermissionGroupPage.permissions.managePermissionGroupsInfoText'
          ),
        },
        {
          type: PERMISSION_TYPE.SYSTEM,
          permissionCode: CLIENT_PERMISSION_CODES_CONSTANTS.USERS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece16',
          name: t('addNewPermissionGroupPage.permissions.manageUsersTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.manageUsersDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.CLIENT],
          infoText: t(
            'addNewPermissionGroupPage.permissions.manageUsersInfoText'
          ),
        },
      ],
      [PERMISSION_TYPE.MANAGE_PROJECT]: [
        {
          type: PERMISSION_TYPE.MANAGE_PROJECT,
          permissionCode:
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.EDIT_PROJECT_PROFILE,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece48',
          name: t(
            'addNewPermissionGroupPage.permissions.editProjectProfileTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.editProjectProfileDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
          infoText: t(
            'addNewPermissionGroupPage.permissions.editProjectProfileInfoText'
          ),
        },
        {
          type: PERMISSION_TYPE.MANAGE_PROJECT,
          permissionCode:
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.CREATE_NEW_PROJECT,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece50',
          name: t(
            'addNewPermissionGroupPage.permissions.createNewProjectsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.createNewProjectsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.CLIENT],
          infoText: t(
            'addNewPermissionGroupPage.permissions.createNewProfilesInfoText'
          ),
          disableToolTip: true,
        },
        {
          type: PERMISSION_TYPE.MANAGE_PROJECT,
          permissionCode:
            MANAGE_PROJECT_PERMISSION_CODES_CONSTANTS.MANAGE_PROJECT_SETTINGS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece47',
          name: t(
            'addNewPermissionGroupPage.permissions.manageProjectSettingsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.manageProjectSettingsDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
          infoText: t(
            'addNewPermissionGroupPage.permissions.manageProjectSettingsInfoText'
          ),
        },
      ],
      [PERMISSION_TYPE.REPORTS]: [
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_COMPLETED_MAPPED_DATA_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece31',
          name: t(
            'addNewPermissionGroupPage.permissions.completedMappedDataTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.completedMappedDataDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_CONCIERGE_REPORTS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece13',
          name: t('addNewPermissionGroupPage.permissions.customReportsTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.customReportsDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_CONCIERGE_REPORTS_CREATE,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece14',
          name: t(
            'addNewPermissionGroupPage.permissions.customReportsUploadTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.customReportsUploadDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PRODUCTION_METRICS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece08',
          name: t(
            'addNewPermissionGroupPage.permissions.highLevelProductionMetricsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.highLevelProductionMetricsDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INTAKE_AUDIT_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece02',
          name: t('addNewPermissionGroupPage.permissions.intakeAuditTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.intakeAuditDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode: REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INVOICE_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece09',
          name: t('addNewPermissionGroupPage.permissions.InvoiceReportTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.InvoiceReportDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_EXCEPTIONS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece05',
          name: t(
            'addNewPermissionGroupPage.permissions.processingExceptionsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.processingExceptionsDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_IN_PROGRESS_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece03',
          name: t(
            'addNewPermissionGroupPage.permissions.processingInProgressTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.processingInProgressDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROCESSING_SUMMARY_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece04',
          name: t(
            'addNewPermissionGroupPage.permissions.processingSummaryTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.processingSummaryDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_INTERACTIVE_VOLUME_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece06',
          name: t(
            'addNewPermissionGroupPage.permissions.reviewWorkspaceCountsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.reviewWorkspaceCountsDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_USER_SUMMARY_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece07',
          name: t(
            'addNewPermissionGroupPage.permissions.reviewWorkspaceUsersTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.reviewWorkspaceUsersDescription'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
        {
          type: PERMISSION_TYPE.REPORTS,
          permissionCode:
            REPORT_PERMISSION_CODES_CONSTANTS.REPORT_PROJECT_TRACKER_VIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece11',
          name: t('addNewPermissionGroupPage.permissions.projectTrackerTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.projectTrackerDescription'
          ),
          footerNotes: t(
            'addNewPermissionGroupPage.permissions.availableToConsilioOnly'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
      ],
      [PERMISSION_TYPE.BUDGET]: [
        {
          type: PERMISSION_TYPE.BUDGET,
          permissionCode: BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece21',
          name: t(
            'addNewPermissionGroupPage.permissions.rightsToViewEstimatedBudgetConsumptionTitle'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.BUDGET,
          permissionCode: BUDGET_ESTIMATED.REPORT_BUDGET_ALERT,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece22',
          name: t(
            'addNewPermissionGroupPage.permissions.showBudgetAlertsTitle'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
      ],
      [PERMISSION_TYPE.ECI]: [
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_SEARCHES,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece32',
          name: t('addNewPermissionGroupPage.permissions.searchesTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.searchesDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCHES,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece33',
          name: t('addNewPermissionGroupPage.permissions.savedSearchesTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.savedSearchesDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_SEARCH_TERM_REPORT,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece37',
          name: t(
            'addNewPermissionGroupPage.permissions.searchTermReportsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.searchTermReportsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_PREVIEW,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece35',
          name: t('addNewPermissionGroupPage.permissions.documentPreviewTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.documentPreviewDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode:
            ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_SAVED_SEARCHES,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece36',
          name: t(
            'addNewPermissionGroupPage.permissions.manageSavedSearchesTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.manageSavedSearchesDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },

        {
          type: PERMISSION_TYPE.ECI,
          permissionCode:
            ECA_PERMISSION_CODES_CONSTANTS.ECI_SAVED_SEARCH_TEMPLATES,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece34',
          name: t(
            'addNewPermissionGroupPage.permissions.savedSearchTemplatesTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.savedSearchTemplatesDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_AI_INVESTIGATE,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece38',
          name: t('addNewPermissionGroupPage.permissions.aiInvestigateTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.aiInvestigateDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_CREATE_DOC_SUMMARY,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece39',
          name: t(
            'addNewPermissionGroupPage.permissions.documentSummaryGenerationTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.documentSummaryGenerationDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_DOCUMENT_TAGGING,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece40',
          name: t(
            'addNewPermissionGroupPage.permissions.taggingDocumentsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.taggingDocumentsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_TAG_FIELDS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece43',
          name: t(
            'addNewPermissionGroupPage.permissions.createEditTagFieldsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.createEditTagFieldsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode:
            ECA_PERMISSION_CODES_CONSTANTS.ECI_MANAGE_CUSTOM_FIELDS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece44',
          name: t(
            'addNewPermissionGroupPage.permissions.createEditCustomFieldsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.createEditCustomFieldsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_EXPORTS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece45',
          name: t(
            'addNewPermissionGroupPage.permissions.exportsPromotionsTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.exportsPromotionsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_INDEXING,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece41',
          name: t(
            'addNewPermissionGroupPage.permissions.indexingElasticAndNLPTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.indexingElasticAndNLPDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_OVERLAYS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece42',
          name: t('addNewPermissionGroupPage.permissions.overlaysTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.overlaysDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.ECI,
          permissionCode: ECA_PERMISSION_CODES_CONSTANTS.ECI_AUDIT_REPORTS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece46',
          name: t('addNewPermissionGroupPage.permissions.auditReportsTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.auditReportsDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
      ],
      [PERMISSION_TYPE.EVIDENCE]: [
        {
          type: PERMISSION_TYPE.EVIDENCE,
          permissionCode: EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece28',
          name: t(
            'addNewPermissionGroupPage.permissions.manageProjectEvidenceTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.manageProjectEvidenceDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.EVIDENCE,
          permissionCode:
            EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece29',
          name: t('addNewPermissionGroupPage.permissions.uploadEvidenceTitle'),
          description: t(
            'addNewPermissionGroupPage.permissions.uploadEvidenceDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
        {
          type: PERMISSION_TYPE.EVIDENCE,
          permissionCode: EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece30',
          name: t(
            'addNewPermissionGroupPage.permissions.initiateProcessingTitle'
          ),
          description: t(
            'addNewPermissionGroupPage.permissions.initiateProcessingDescription'
          ),
          category: [PERMISSION_GROUP_CATEGORY.PROJECT],
        },
      ],
      [PERMISSION_TYPE.BUSINESS_INTELLIGENCE]: [
        {
          type: PERMISSION_TYPE.BUSINESS_INTELLIGENCE,
          permissionCode:
            CLIENT_PERMISSION_CODES_CONSTANTS.PROJECT_LEVEL_REPORTS,
          permissionId: '9fbd0e2b-5fe2-4d1f-a6bb-1dba307ece51',
          name: t(
            'addNewPermissionGroupPage.permissions.projectLevelReportsTitle'
          ),
          footerNotes: t(
            'addNewPermissionGroupPage.permissions.projectLevelReportsFooterNotes'
          ),
          category: [
            PERMISSION_GROUP_CATEGORY.CLIENT,
            PERMISSION_GROUP_CATEGORY.PROJECT,
          ],
        },
      ],
    }),
    []
  );

  return {
    permissionMasterConfig,
    permissionList,
  };
};
